import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizePayload = (values = {}) => {
  const nombre = (values.nombre || '').trim();
  const bohRaw = values.boh;
  const bohNumber = bohRaw === '' || bohRaw === null || bohRaw === undefined ? null : Number(bohRaw);
  const boh = Number.isFinite(bohNumber) ? bohNumber : null;
  const divisionRaw = values.division_id;
  const divisionNumber = divisionRaw === '' || divisionRaw === null || divisionRaw === undefined ? null : Number(divisionRaw);
  const division_id = Number.isFinite(divisionNumber) ? divisionNumber : null;
  return {
    nombre,
    boh,
    division_id,
    activo: Boolean(values.activo)
  };
};

const validateBoh = (value) => Number.isInteger(value);
const validateDivision = (value) => Number.isInteger(value);

export const useAdminAreas = () => {
  const [items, setItems] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/areas', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las áreas');
      }
      if (!signal?.aborted) {
        setItems(data.areas || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar áreas');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const fetchDivisions = useCallback(async (signal) => {
    try {
      const response = await fetch('/admin/api/divisions', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las divisiones');
      }
      if (!signal?.aborted) {
        setDivisions(data.divisions || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setDivisions([]);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    fetchDivisions(controller.signal);
    return () => controller.abort();
  }, [fetchList, fetchDivisions, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveArea = useCallback(async (values) => {
    const payload = normalizePayload(values);
    if (!payload.nombre) {
      throw new Error('El nombre del área es requerido.');
    }
    if (payload.boh === null || !validateBoh(payload.boh)) {
      throw new Error('El BoH debe ser un número entero.');
    }
    if (payload.division_id === null || !validateDivision(payload.division_id)) {
      throw new Error('La división es obligatoria.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/areas/${values.id}` : '/admin/api/areas';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar el área');
    }
    return data;
  }, []);

  const deleteArea = useCallback(async (id) => {
    const response = await fetch(`/admin/api/areas/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo desactivar el área');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    divisions,
    loading,
    error,
    refresh,
    saveArea,
    deleteArea
  }), [items, divisions, loading, error, refresh, saveArea, deleteArea]);
};
